import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { apiFetch } from '../lib/api'
import { PRODUCTS, type OverageSummary, type Product, type ProductQuota } from '../lib/types'
import { useProductTab } from '../hooks/useProductTab'
import { useProductStatus } from '../hooks/useProductStatus'
import { useUnsavedGuard } from '../hooks/useUnsavedGuard'
import { useMoney } from '../hooks/useMoney'
import { toast } from '../lib/toast'
import { Tabs } from '../components/ui/Tabs'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { PageHeader } from '../components/ui/PageHeader'
import { Card, CardHeader, CardBody } from '../components/ui/Card'
import { Toggle } from '../components/ui/Toggle'
import { InfoTip } from '../components/ui/InfoTip'
import { SubHead } from '../components/ui/SubHead'
import { PageSaveDiscard } from '../components/ui/PageSaveDiscard'
import { AdminConfirmModal } from '../components/AdminConfirmModal'
import { ExcessBreakdown } from '../components/ExcessBreakdown'
import { ProductViewGate } from '../components/ProductViewGate'
import { QueryState } from '../components/ui/QueryStateCard'
import { useWriteErrorHandler } from '../hooks/useWriteErrorHandler'

type Draft = Record<Product, boolean>

const overageQuery = (product: Product) => ({
  queryKey: ['overage-summary', product],
  queryFn: () =>
    apiFetch<OverageSummary>(`/api/v1/orders/overage-summary?product=${encodeURIComponent(product)}`),
  retry: false,
})

function savedDraft(quota: readonly ProductQuota[]): Draft {
  return {
    'Mambu MCP': quota.find((q) => q.product === 'Mambu MCP')?.overageEnabled ?? false,
    'Mambu Agents': quota.find((q) => q.product === 'Mambu Agents')?.overageEnabled ?? false,
  }
}

/** The prototype's `excessConfirmContent` (subscription-portal.html): ONE
 * wording for both products (OQ-15, ARCHITECTURE_DECISIONS.md §44) — the
 * confirms differ only in the "Charged at" row's rate (T-EXC-07), which is
 * the server's `ratePer1000` in the contract currency (OQ-6(a), OQ-14).
 * Until that rate is known the row says so instead (`rateMissing`, §45). */
function confirmContent(
  t: TFunction,
  product: Product,
  turningOn: boolean,
  rate: string | null,
  rateMissing: string
): {
  title: string
  impactTitle: string
  impactText: string
  bullets: [string, string][]
  confirmLabel: string
} {
  const scope: [string, string] = [t('excessVolume.confirm.row.scope'), t('excessVolume.confirm.scopeValue', { product })]
  const appliesFrom: [string, string] = [t('excessVolume.confirm.row.appliesFrom'), t('excessVolume.confirm.nextCall')]
  if (!turningOn) {
    return {
      title: t('excessVolume.confirm.turnOffTitle', { product }),
      impactTitle: t('excessVolume.confirm.impactTitleOff'),
      impactText: t('excessVolume.confirm.turnOffImpact'),
      bullets: [
        scope,
        appliesFrom,
        [t('excessVolume.confirm.row.furtherCost'), t('excessVolume.confirm.none')],
        [t('excessVolume.confirm.row.reversible'), t('excessVolume.confirm.reversibleOff')],
      ],
      confirmLabel: t('excessVolume.confirm.turnOffBtn'),
    }
  }
  const chargedAtKey = product === 'Mambu MCP' ? 'excessVolume.confirm.chargedAtMcp' : 'excessVolume.confirm.chargedAtAgt'
  return {
    title: t('excessVolume.confirm.turnOnTitle', { product }),
    impactTitle: t('excessVolume.confirm.impactTitleOn'),
    impactText: t('excessVolume.confirm.turnOnImpact'),
    bullets: [
      scope,
      appliesFrom,
      [
        t('excessVolume.confirm.row.chargedAt'),
        rate === null ? rateMissing : t(chargedAtKey, { rate }),
      ],
      [t('excessVolume.confirm.row.reversible'), t('excessVolume.confirm.reversibleOn')],
    ],
    confirmLabel: t('excessVolume.confirm.turnOnBtn'),
  }
}

// Its own page, matching the prototype's `section-excess` (`renderExcess` /
// `renderAgtExcess`, subscription-portal.html): one card per product, two
// subsections — the setting ("Excess volume" sub-head, the toggle and its
// info note), then what it has come to ("Status": four KPIs and the month
// table). The invoice note is on the card title (T-EXC-13).
//
// Both products are edited as one draft and saved together, matching the
// prototype's `excessDraft`/`setExcessDirty` — a reader can flip both
// products' toggles before committing, and Save fires one admin
// confirmation per product actually changed, chained. Discard asks first,
// and leaving the page with edits goes through the shared unsaved-changes
// guard (OQ-16).
export function ExcessVolume() {
  const { t } = useTranslation()
  const money = useMoney()
  const { product, setProduct } = useProductTab('excess-volume')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirmQueue, setConfirmQueue] = useState<Product[]>([])
  const savedInRun = useRef(false)
  // The confirmed PUT is in flight: the modal goes inert, and the ref stops a
  // second click that lands before the re-render from running it twice (each
  // run advances the queue, which would skip the next product's confirm).
  const [committing, setCommitting] = useState(false)
  const committingRef = useRef(false)
  const queryClient = useQueryClient()
  const onWriteError = useWriteErrorHandler()

  const status = useProductStatus(product)
  const { quota } = status
  // Excess Volume is the full page on a live contract only; Trial, Expired
  // and Not Contracted are the preview card (spec §4.1b-excess; MS-819 Q4,
  // superseding ARCHITECTURE_DECISIONS.md §10).
  const isFull = (p: Product) => status.view('excess-volume', p) === 'full'
  const {
    data: overage,
    isError: overageFailed,
    refetch: refetchOverage,
  } = useQuery({ ...overageQuery(product), enabled: isFull(product) })

  const confirming = confirmQueue[0]
  // The "Charged at" rate of the product being confirmed, which need not be
  // the tab on screen (both products save together).
  const { data: confirmingOverage, isError: rateFailed } = useQuery({
    ...overageQuery(confirming ?? product),
    enabled: confirming !== undefined,
  })

  // Seed the draft from the server once quota has loaded — this is the same
  // "one draft per page load" model the prototype's excessDraft object uses.
  useEffect(() => {
    if (quota && !draft) setDraft(savedDraft(quota))
  }, [quota, draft])

  const dirty =
    !!draft &&
    !!quota &&
    // Only a product whose page is editable can be dirty or saved — a
    // previewed product's toggle is never on screen (MS-819 save filter).
    quota.some((q) => isFull(q.product) && draft[q.product] !== q.overageEnabled)

  // The prototype's page controller `clearDirty`: back to what is saved, no
  // prompt — the guard has already asked.
  function resetDraft() {
    if (quota) setDraft(savedDraft(quota))
  }
  useUnsavedGuard(dirty, resetDraft)

  function discard() {
    if (!window.confirm(t('excessVolume.discardConfirm'))) return
    resetDraft()
  }

  function startSave() {
    if (!quota || !draft) return
    const changed = quota
      .filter((q) => isFull(q.product) && draft[q.product] !== q.overageEnabled)
      .map((q) => q.product)
    if (!changed.length) return
    savedInRun.current = false
    setConfirmQueue(changed)
  }

  // Move to the next queued confirmation; after the last one, confirm the
  // save with the prototype's toast — once something was actually saved.
  function advance() {
    if (confirmQueue.length <= 1 && savedInRun.current) {
      savedInRun.current = false
      toast(t('excessVolume.savedToast'))
    }
    setConfirmQueue((q) => q.slice(1))
  }

  async function commitOne(p: Product) {
    if (!draft || committingRef.current) return
    committingRef.current = true
    setCommitting(true)
    try {
      await apiFetch(`/api/v1/quota/${encodeURIComponent(p)}/overage`, {
        method: 'PUT',
        body: JSON.stringify({ overageEnabled: draft[p] }),
      })
      savedInRun.current = true
      await queryClient.invalidateQueries({ queryKey: ['quota'] })
      advance()
    } catch (error) {
      // Stop the queue: the rest were confirmed against the same stale view.
      setConfirmQueue([])
      await onWriteError(error)
    } finally {
      committingRef.current = false
      setCommitting(false)
    }
  }

  // Turning excess on authorises a charge, so its confirm never goes ahead
  // without the rate it charges at: "Loading…" while the read is in flight,
  // "unavailable" when it failed or no price table is in force today, and
  // Confirm held in both cases (ARCHITECTURE_DECISIONS.md §45).
  const rate = confirmingOverage?.ratePer1000 ?? null
  const rateMissing = rateFailed || confirmingOverage ? t('excessVolume.confirm.rateUnavailable') : t('common.loading')
  const turningOn = confirming !== undefined && !!draft?.[confirming]
  const rateText = rate === null ? null : money(Number(rate), 2)
  const content = confirming && draft ? confirmContent(t, confirming, draft[confirming], rateText, rateMissing) : null

  return (
    <PageView>
      <PageHeader
        title={t('excessVolume.title')}
        subtitle={t('excessVolume.subtitle')}
        actions={<PageSaveDiscard dirty={dirty} onSave={startSave} onDiscard={discard} />}
      />

      <Tabs value={product} onChange={setProduct} options={PRODUCTS} />

      <PageScrollArea>
        <ProductViewGate status={status} page="excess-volume" product={product}>
          {() =>
            draft && (
              <Card collapsible>
                <CardHeader
                  title={
                    <>
                      {t('excessVolume.cardTitle')}
                      <InfoTip text={t('excessVolume.invoiceNote')} placement="below" align="left" width={320} />
                    </>
                  }
                  subtitle={
                    <Trans
                      i18nKey={product === 'Mambu MCP' ? 'excessVolume.captionMcp' : 'excessVolume.captionAgt'}
                      components={{ strong: <strong /> }}
                    />
                  }
                />
                <CardBody>
                  <SubHead label={t('excessVolume.toggleSubhead')} />
                  <div className="flex items-center gap-3">
                    <span className="flex flex-1 items-center text-[13px] text-[var(--text-primary)]">
                      {t('excessVolume.toggleLabel')}
                      <InfoTip text={t('excessVolume.toggleInfo')} placement="below" align="left" width={300} />
                    </span>
                    <Toggle
                      checked={draft[product]}
                      ariaLabel={t('excessVolume.toggleLabel')}
                      onChange={(next) => setDraft((d) => (d ? { ...d, [product]: next } : d))}
                    />
                  </div>
                  <div className="mt-[22px] pt-[22px] border-t border-[var(--border-light)]">
                    <SubHead label={t('excessVolume.statusTitle')} />
                    {overageFailed && <QueryState state="error" onRetry={() => refetchOverage()} />}
                    {!overageFailed && !overage && <QueryState state="pending" />}
                    {!overageFailed && overage && (
                      <ExcessBreakdown
                        periodStart={overage.periodStart}
                        periodEnd={overage.periodEnd}
                        excessVolume={overage.excessThisPeriod}
                        excessCost={Number(overage.excessCost)}
                        months={overage.months}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            )
          }
        </ProductViewGate>
      </PageScrollArea>

      {confirming && content && (
        <AdminConfirmModal
          key={confirming}
          title={content.title}
          subtitle={t('excessVolume.confirm.subtitle')}
          impactTitle={content.impactTitle}
          impactText={content.impactText}
          bullets={content.bullets}
          checkLabel={t('excessVolume.confirm.checkLabel')}
          confirmLabel={content.confirmLabel}
          pending={committing}
          confirmDisabled={turningOn && rate === null}
          onConfirm={() => commitOne(confirming)}
          onCancel={() => {
            // Cancelling one product's confirmation reverts just that
            // product's draft back to its saved value and moves on to the
            // next queued product, matching the prototype's onCancel chain.
            setDraft((d) => {
              if (!d || !quota) return d
              return { ...d, [confirming]: savedDraft(quota)[confirming] }
            })
            advance()
          }}
        />
      )}
    </PageView>
  )
}
